import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaTrashAlt, FaPlus, FaMinus } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

const CartDrawer = ({ show, onClose, onCartChange }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const CART_KEY = "cart";

  const readCart = () => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      console.error("readCart failed", e);
      return [];
    }
  };

  const writeCart = (arr) => {
    try {
      // keep storage in sync
      localStorage.setItem(CART_KEY, JSON.stringify(arr));
      // notify parent header (number of product entries)
      if (onCartChange) onCartChange(arr.length);
    } catch (e) {
      console.error("writeCart failed", e);
    }
  };

  // Sync helper: read from storage and update items state
  const syncFromStorage = () => {
    const c = readCart();
    setItems(c);
    // also notify parent so header always matches
    if (onCartChange) onCartChange(c.length);
  };

  // initialize and listen for external storage changes
  useEffect(() => {
    // initial sync when component mounts
    syncFromStorage();

    const onStorage = (e) => {
      if (!e) return;
      // if cart changed in another tab / component, sync
      if (e.key === CART_KEY) {
        syncFromStorage();
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // also re-sync whenever the drawer is opened (ensures latest data)
  useEffect(() => {
    if (show) {
      syncFromStorage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const removeItem = (productId, categorySizeId = null) => {
    const current = readCart();
    const filtered = current.filter(
      (it) =>
        !(
          String(it.productId) === String(productId) &&
          String(it.categorySizeId) === String(categorySizeId)
        )
    );
    writeCart(filtered);
    setItems(filtered);
  };

  const changeItemQuantity = (productId, categorySizeId, delta) => {
    const current = readCart();

    const updated = current.map((it) => {
      if (
        String(it.productId) === String(productId) &&
        String(it.categorySizeId) === String(categorySizeId)
      ) {
        const newQty = Math.max(0, Number(it.quantity || 0) + delta);
        const unit = Number(it.price ?? it.unitPrice ?? 0); // per-unit price fallback
        return {
          ...it,
          quantity: newQty,
          // store numeric totalPrice (not padded string)
          totalPrice: Number((unit * newQty).toFixed(2)),
        };
      }
      return it;
    });

    // remove zero-qty items
    const cleaned = updated.filter((it) => Number(it.quantity || 0) > 0);

    // write cleaned cart and update UI from storage (source of truth)
    writeCart(cleaned);
    setItems(cleaned);
  };

  // totals: totalPrice numeric and totalQty = number of product entries
  const totalPrice = items
    .reduce((acc, it) => acc + Number(it.totalPrice || 0), 0)
    .toFixed(2);
  const totalQty = items.length; // number of distinct items (entries)

  const handleCheckout = () => {
    onClose?.();
    navigate("/user/login");
  };

  return (
    <>
      <div
        className={`cart-drawer-backdrop ${show ? "visible" : ""}`}
        onClick={onClose}
      />
      <aside
        className={`cart-drawer ${show ? "open" : ""}`}
        aria-hidden={!show}
      >
        <div className="cart-drawer-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">
              My Cart <small className="text-muted">({totalQty})</small>
            </h5>
          </div>

          <button
            type="button"
            className="btn btn-link p-0 m-0 cart-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <IoClose size={22} />
          </button>
        </div>

        <div className="cart-drawer-body">
          {items.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-1">
                <strong>Your cart is empty</strong>
              </p>
              <Link to="/home" className="small text-muted">
                Browse products
              </Link>
            </div>
          ) : (
            <div className="cart-items-list">
              {items.map((item, idx) => {
                const imageUrl = item.productImage
                  ? `https://goldenhides.in/api/product/${item.productImage}`
                  : "";
                const sizeReadable = item.size || item.categorySizeId || "";

                return (
                  <div className="cart-item-card" key={idx}>
                    <div className="cart-item-inner d-flex">
                      {/* thumbnail */}
                      <div className="cart-item-thumb">
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.productName} />
                        ) : (
                          <div className="placeholder-img" />
                        )}
                      </div>

                      {/* details and qty */}
                      <div className="cart-item-main flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div
                              className="cart-item-title"
                              style={{ cursor: "pointer", fontWeight: "600" }}
                              onClick={() => {
                                if (item.categoryId && item.categoryName) {
                                  navigate(
                                    `/product/${item.productId}/category/${item.categoryId}`
                                  );
                                  onClose?.();
                                }
                              }}
                            >
                              {item.productName || "-"}
                            </div>

                            <div className="cart-item-meta small text-muted">
                              Qty: {Number(item.quantity || 0)} &middot;{" "}
                              {item.size || "-"}
                            </div>
                          </div>

                          <div className="text-end">
                            <div className="cart-item-price">
                              ₹ {Number(item.totalPrice || 0).toFixed(2)}
                            </div>
                            <button
                              className="btn btn-link text-danger p-0 cart-remove-btn"
                              onClick={() =>
                                removeItem(item.productId, item.categorySizeId)
                              }
                              aria-label="Delete"
                              title="Remove"
                            >
                              <FaTrashAlt size={16} />
                            </button>
                          </div>
                        </div>

                        {/* qty controls */}
                        <div className="qty-controls mt-3">
                          <button
                            className="qty-btn"
                            onClick={() =>
                              changeItemQuantity(
                                item.productId,
                                item.categorySizeId,
                                -1
                              )
                            }
                            aria-label="Decrease quantity"
                          >
                            <FaMinus />
                          </button>
                          <div className="qty-number">
                            {Number(item.quantity || 0)}
                          </div>
                          <button
                            className="qty-btn"
                            onClick={() =>
                              changeItemQuantity(
                                item.productId,
                                item.categorySizeId,
                                1
                              )
                            }
                            aria-label="Increase quantity"
                          >
                            <FaPlus />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="cart-drawer-footer">
          <div className="text-center mt-2 mb-3">
            <img
              src="/assets/payment_image.png"
              alt="Available payment methods"
              className="payment-logos-simple"
            />
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2 totals-row">
            <div>
              <div className="small text-muted">Items</div>
              <strong>{totalQty}</strong>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="small text-muted">Total</div>
              <strong>₹ {totalPrice}</strong>
            </div>
          </div>

          <div>
            <button
              className="btn proceed-btn w-100 py-2"
              onClick={handleCheckout}
              disabled={items.length === 0}
              aria-label="Proceed to Buy"
            >
              Proceed to Buy
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default CartDrawer;
